import '@fontsource/vazirmatn/400.css';
import '@fontsource/vazirmatn/500.css';
import '@fontsource/vazirmatn/600.css';
import '@fontsource/vazirmatn/700.css';
import '@fontsource/vazirmatn/800.css';
import '../css/site.css';
import 'sweetalert2/dist/sweetalert2.min.css';
import { initializePersianDatePickers } from './persian-datepicker.js';
import { hydrateLocalDateTimes } from './utc-time.js';
import './tournament-cards.js';

hydrateLocalDateTimes();
document.querySelectorAll('[data-timezone-offset]').forEach(input => {
  input.value = String(new Date().getTimezoneOffset());
});
document.querySelectorAll('a[data-local-time-export]').forEach(link => {
  const url = new URL(link.href, location.href);
  url.searchParams.set('timeZoneOffsetMinutes', String(new Date().getTimezoneOffset()));
  link.href = url.toString();
});
document.querySelectorAll('input[type="datetime-local"][data-local-now]').forEach(input => {
  if (input.value) return;
  const now = new Date();
  const pad = value => String(value).padStart(2, '0');
  input.value = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
});

const root = document.documentElement;
const systemTheme = matchMedia('(prefers-color-scheme: dark)');
const themeMeta = document.querySelector('[data-theme-color]');

function preferredTheme() {
  const saved = localStorage.getItem('chess-academy-theme');
  if (saved === 'light' || saved === 'dark') return saved;
  return 'dark';
}

function syncThemeUi(theme) {
  const dark = theme === 'dark';
  themeMeta?.setAttribute('content', dark ? '#141815' : '#f3f5f4');
  document.querySelectorAll('[data-theme-toggle]').forEach(button => {
    const label = dark ? 'فعال‌کردن تم روشن' : 'فعال‌کردن تم تاریک';
    button.setAttribute('aria-label', label);
    button.setAttribute('title', label);
    const text = button.querySelector('[data-theme-label]');
    if (text) text.textContent = label;
  });
}

function applyTheme(theme, persist = true) {
  root.dataset.theme = theme;
  if (persist) localStorage.setItem('chess-academy-theme', theme);
  syncThemeUi(theme);
}

applyTheme(root.dataset.theme || preferredTheme(), false);
systemTheme.addEventListener('change', () => {
  if (!localStorage.getItem('chess-academy-theme')) applyTheme(preferredTheme(), false);
});
document.querySelectorAll('[data-theme-toggle]').forEach(button => {
  button.addEventListener('click', () => applyTheme(root.dataset.theme === 'dark' ? 'light' : 'dark'));
});

const mobileDrawer = document.querySelector('[data-mobile-drawer]');
const menuButton = document.querySelector('[data-menu-toggle]');
const mobileBackdrop = document.querySelector('[data-mobile-menu-backdrop]');
const mobileCloseButton = document.querySelector('[data-menu-close]');
const pageMain = document.querySelector('body > main');
const siteFooter = document.querySelector('body > .site-footer');
const mobileMenuMedia = matchMedia('(max-width: 1199px)');
let menuReturnFocus = null;
const drawerBackground = [...document.querySelectorAll('body > .site-header, body > main, body > .site-footer, body > .active-games-rail, body > .global-notification-wrap')];
const drawerPriorInert = new Map();

function drawerFocusable() {
  if (!mobileDrawer) return [];
  return [...mobileDrawer.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])')]
    .filter(element => !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0);
}

function setMobileMenu(open) {
  if (!mobileDrawer) return;
  open = Boolean(open && mobileMenuMedia.matches);
  if (open && !mobileDrawer.classList.contains('open')) {
    menuReturnFocus = document.activeElement;
    drawerBackground.forEach(element => drawerPriorInert.set(element, element.inert));
  }
  mobileDrawer.classList.toggle('open', open);
  mobileBackdrop?.classList.toggle('open', open);
  document.body.classList.toggle('mobile-menu-open', open);
  menuButton?.setAttribute('aria-expanded', String(open));
  mobileDrawer.setAttribute('aria-hidden', String(!open));
  mobileDrawer.inert = !open;
  drawerBackground.forEach(element => { element.inert = open || (drawerPriorInert.get(element) ?? false); });
  if (!open) drawerPriorInert.clear();
  if (open) {
    mobileDrawer.focus({ preventScroll: true });
  } else if (mobileMenuMedia.matches && menuReturnFocus instanceof HTMLElement) {
    menuReturnFocus.focus({ preventScroll: true });
  }
}

setMobileMenu(false);

menuButton?.addEventListener('click', () => setMobileMenu(!mobileDrawer?.classList.contains('open')));
mobileCloseButton?.addEventListener('click', () => setMobileMenu(false));
mobileBackdrop?.addEventListener('click', () => setMobileMenu(false));
mobileDrawer?.querySelectorAll('a').forEach(link => link.addEventListener('click', () => setMobileMenu(false)));

mobileDrawer?.addEventListener('keydown', event => {
  if (event.key !== 'Tab') return;
  const focusable = drawerFocusable();
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && (document.activeElement === first || document.activeElement === mobileDrawer)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === mobileDrawer)) {
    event.preventDefault();
    first.focus();
  }
});

const navGroups = [...document.querySelectorAll('.site-header .nav-group')];

function fitNavMenu(group) {
  const menu = group?.querySelector('.nav-group-menu');
  if (!menu || !group.open) return;

  menu.style.removeProperty('--nav-menu-offset-x');
  const viewportWidth = document.documentElement.clientWidth;
  const viewportGutter = 12;
  const rect = menu.getBoundingClientRect();
  let offset = 0;

  if (rect.left < viewportGutter) offset += viewportGutter - rect.left;
  if (rect.right + offset > viewportWidth - viewportGutter) {
    offset -= rect.right + offset - (viewportWidth - viewportGutter);
  }

  menu.style.setProperty('--nav-menu-offset-x', `${Math.round(offset)}px`);
}

navGroups.forEach(group => {
  group.addEventListener('toggle', () => {
    if (!group.open) {
      group.querySelector('.nav-group-menu')?.style.removeProperty('--nav-menu-offset-x');
      return;
    }
    navGroups.forEach(other => { if (other !== group) other.open = false; });
    requestAnimationFrame(() => fitNavMenu(group));
  });
  if (group.open) requestAnimationFrame(() => fitNavMenu(group));
});
document.addEventListener('click', event => {
  if (!event.target.closest('.nav-group')) navGroups.forEach(group => { group.open = false; });
});
window.addEventListener('resize', () => {
  navGroups.forEach(group => { if (group.open) fitNavMenu(group); });
}, { passive: true });

const adminMenuMedia = matchMedia('(max-width: 900px)');
const adminShell = document.querySelector('[data-admin-shell]');
const adminMenuButton = document.querySelector('[data-admin-menu]');
const adminSidebar = document.querySelector('[data-admin-sidebar]');
const adminBackdrop = document.querySelector('[data-admin-sidebar-backdrop]');
const adminCollapse = document.querySelector('[data-admin-collapse]');
let adminMenuReturnFocus = null;

if (adminShell) {
  const compact = localStorage.getItem('kishmat-admin-compact') === '1';
  adminShell.classList.toggle('admin-compact', compact);
  adminCollapse?.setAttribute('aria-expanded', String(!compact));
}

function setAdminDrawer(open) {
  if (!adminShell) return;
  open = Boolean(open && adminMenuMedia.matches);
  if (open) adminMenuReturnFocus = document.activeElement;
  adminShell.classList.toggle('admin-menu-open', open);
  document.body.classList.toggle('admin-mobile-menu-open', open);
  adminMenuButton?.setAttribute('aria-expanded', String(open));
  if (adminSidebar) {
    const hidden = adminMenuMedia.matches && !open;
    adminSidebar.inert = hidden;
    adminSidebar.setAttribute('aria-hidden', String(hidden));
  }
  if (open) {
    adminSidebar?.focus?.({ preventScroll: true });
  } else if (adminMenuMedia.matches && adminMenuReturnFocus instanceof HTMLElement) {
    adminMenuReturnFocus.focus({ preventScroll: true });
  }
}

setAdminDrawer(false);

adminMenuButton?.addEventListener('click', () => setAdminDrawer(!adminShell?.classList.contains('admin-menu-open')));
adminBackdrop?.addEventListener('click', () => setAdminDrawer(false));
adminCollapse?.addEventListener('click', () => {
  if (!adminShell) return;
  const compact = adminShell.classList.toggle('admin-compact');
  localStorage.setItem('kishmat-admin-compact', compact ? '1' : '0');
  adminCollapse.setAttribute('aria-expanded', String(!compact));
  adminCollapse.setAttribute('aria-label', compact ? 'بازکردن سایدبار' : 'جمع‌کردن سایدبار');
});
adminSidebar?.querySelectorAll('a').forEach(link => link.addEventListener('click', () => setAdminDrawer(false)));

document.addEventListener('keydown', event => {
  if (event.key !== 'Escape') return;
  navGroups.forEach(group => { if (group.open) { group.open = false; group.querySelector('summary')?.focus(); } });
  if (mobileDrawer?.classList.contains('open')) setMobileMenu(false);
  if (adminShell?.classList.contains('admin-menu-open')) setAdminDrawer(false);
});

adminMenuMedia.addEventListener('change', () => setAdminDrawer(false));
mobileMenuMedia.addEventListener('change', () => {
  setMobileMenu(false);
  setAdminDrawer(false);
});

function updateVisualViewport() {
  const viewport = window.visualViewport;
  const height = viewport?.height ?? window.innerHeight;
  const keyboardOffset = Math.max(0, window.innerHeight - height - (viewport?.offsetTop ?? 0));
  root.style.setProperty('--visual-viewport-height', height + 'px');
  root.style.setProperty('--keyboard-offset', keyboardOffset + 'px');
  document.body.classList.toggle('mobile-keyboard-open', keyboardOffset > 120);
}
updateVisualViewport();
window.visualViewport?.addEventListener('resize', updateVisualViewport, { passive: true });
window.visualViewport?.addEventListener('scroll', updateVisualViewport, { passive: true });
window.addEventListener('resize', updateVisualViewport, { passive: true });

document.addEventListener('submit', event => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || event.defaultPrevented || form.matches('[data-no-submit-state]')) return;
  if (!form.checkValidity()) return;
  const submitter = event.submitter;
  form.classList.add('is-submitting');
  form.setAttribute('aria-busy', 'true');
  if (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) {
    submitter.classList.add('is-loading');
    submitter.setAttribute('aria-disabled', 'true');
  }
});

document.querySelectorAll('[data-login-password-toggle]').forEach(button => {
  button.addEventListener('click', () => {
    const input = button.closest('.password-input-wrap')?.querySelector('input');
    if (!(input instanceof HTMLInputElement)) return;
    const visible = input.type === 'text';
    input.type = visible ? 'password' : 'text';
    button.setAttribute('aria-pressed', String(!visible));
    button.setAttribute('aria-label', visible ? 'نمایش رمز عبور' : 'پنهان‌کردن رمز عبور');
    button.textContent = visible ? 'نمایش' : 'پنهان';
  });
});

initializePersianDatePickers();

// Installability is progressive: the button appears only when the browser can
// install, or on iOS where Add to Home Screen is exposed through the Share UI.
const installButtons = [...document.querySelectorAll('[data-pwa-install]')];
let pendingInstallPrompt = null;
const standalone = matchMedia('(display-mode: standalone)').matches
  || window.navigator.standalone === true;
const iosInstall = /iphone|ipad|ipod/i.test(navigator.userAgent) && !standalone;

function setInstallVisible(visible) {
  installButtons.forEach(button => { button.hidden = !visible; });
}

if (iosInstall) setInstallVisible(true);
window.addEventListener('beforeinstallprompt', event => {
  event.preventDefault();
  pendingInstallPrompt = event;
  setInstallVisible(true);
});
window.addEventListener('appinstalled', () => {
  pendingInstallPrompt = null;
  setInstallVisible(false);
});
installButtons.forEach(button => button.addEventListener('click', async () => {
  if (pendingInstallPrompt) {
    pendingInstallPrompt.prompt();
    await pendingInstallPrompt.userChoice;
    pendingInstallPrompt = null;
    setInstallVisible(false);
    return;
  }
  if (iosInstall) window.alert('برای نصب کیش مات، منوی Share مرورگر را باز کنید و «Add to Home Screen» را بزنید.');
}));

if ('serviceWorker' in navigator && window.isSecureContext) {
  window.addEventListener('load', () => {
    const basePath = (document.documentElement.dataset.basePath || '').replace(/\/$/, '');
    navigator.serviceWorker.register(`${basePath}/service-worker.js`, {
      scope: `${basePath || ''}/`,
      updateViaCache: 'none'
    }).then(registration => registration.update()).catch(() => {
      // The site remains fully functional when installation is unavailable.
    });
  }, { once: true });
}
